import { useInitResetPasswordMutation, useResetPasswordMutation } from '@generated/graphql';
import { Button, Stack, Typography } from '@mui/material';
import { useToast } from '@myhrbox/myhrbox-frontend-core';
import ROUTES from 'constants/routes';
import AuthSplitShell from 'pages/onboarding/components/AuthSplitShell';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import InitResetForm, { InitResetFormData } from './InitResetForm';
import ResetPasswordForm, { ResetPasswordFormData } from './ResetPasswordForm';

const PasswordReset = () => {
  const { t } = useTranslation();
  const { showErrorToast, showSuccessToast } = useToast();
  const navigate = useNavigate();
  const [step, setStep] = useState<'init' | 'reset'>('init');

  const [initResetPassword, { loading: initLoading }] = useInitResetPasswordMutation();
  const [resetPassword, { loading: resetLoading }] = useResetPasswordMutation();

  const onInitSubmit = ({ email }: InitResetFormData) => {
    initResetPassword({ variables: { email } })
      .then((res) => {
        if (!res.data?.initResetPassword) throw new Error('initResetPassword failed');
        setStep('reset');
      })
      .catch(() => {
        showErrorToast(t('errors.common'));
      });
  };

  const onResetSubmit = ({ code, password }: ResetPasswordFormData) => {
    resetPassword({ variables: { code, password } })
      .then((res) => {
        if (!res.data?.resetPassword) throw new Error('resetPassword failed');
        showSuccessToast(t('passwordReset.successMessage'));
        navigate(ROUTES.LOGIN);
      })
      .catch(() => {
        showErrorToast(t('errors.common'));
      });
  };

  return (
    <AuthSplitShell>
      <Stack spacing={{ xs: 4, lg: 4.5 }}>
        <Stack spacing={3}>
          <Stack spacing={1.5}>
            <Typography variant='h28'>{t('passwordReset.title')}</Typography>
            <Typography component='p' variant='subtitle18rg'>
              {step === 'init' ? t('passwordReset.subtitleInit') : t('passwordReset.subtitleReset')}
            </Typography>
          </Stack>

          {step === 'init' ? (
            <InitResetForm onSubmit={onInitSubmit} loading={initLoading} />
          ) : (
            <ResetPasswordForm onSubmit={onResetSubmit} loading={resetLoading} />
          )}

          <Button
            variant='text'
            component={RouterLink}
            to={ROUTES.LOGIN}
            sx={{
              width: 'fit-content',
              minWidth: 0,
              px: 0,
              fontSize: 16,
              fontWeight: 600,
              mx: 'auto !important',
            }}>
            {t('passwordReset.backToLogin')}
          </Button>
        </Stack>
      </Stack>
    </AuthSplitShell>
  );
};

export default PasswordReset;
